import type { MaybeRefOrGetter } from 'vue';

interface IpxBackgroundStyleOptions {
  /**
   * Final ipx target width in px. Set it to the element's rendered CSS width multiplied by
   * the device-pixel-ratio you target (2 for retina). Pass a ref/getter for cards whose
   * width varies by variant.
   */
  width: MaybeRefOrGetter<number>;
  /** WebP quality, 0-100. Defaults to 80. */
  quality?: number;
}

/**
 * Builds a reactive CSS `background-image` style whose URL is routed through Nuxt ipx —
 * resized and served as avif with a webp fallback — instead of loading the full-resolution
 * source.
 *
 * Use this for EVERY CSS `background-image` fed a remote or dynamic (e.g. blob-hosted) URL.
 * A raw `background-image: url(${src})` bypasses ipx entirely and ships the un-optimised
 * original. (For a plain `<img>`, use `<NuxtPicture format="avif,webp">` instead.)
 *
 * A single `url()` can't content-negotiate formats, so this emits `image-set()`: the browser
 * picks avif where supported and falls back to webp; both variants go through ipx.
 *
 * @example
 * const heroStyle = useIpxBackgroundStyle(() => props.heroImageUrl, { width: 1000 });
 * // <div :style="heroStyle" class="bg-cover bg-center" />
 */
export const useIpxBackgroundStyle = (
  src: MaybeRefOrGetter<string | null | undefined>,
  options: IpxBackgroundStyleOptions,
) => {
  const $img = useImage();

  return computed<{ backgroundImage: string } | undefined>(() => {
    const url = toValue(src);
    if (!url) {
      return undefined;
    }
    const width = toValue(options.width);
    const quality = options.quality ?? 80;
    const variant = (format: 'avif' | 'webp') => $img(url, { width, format, quality });

    return {
      backgroundImage: `image-set(url("${variant('avif')}") type("image/avif"), url("${variant('webp')}") type("image/webp"))`,
    };
  });
};
